<template>
  <div class="am3-collapsible w-full">
    <!-- Header with collapse toggle -->
    <button
      class="w-full flex items-center outline-none justify-between py-2 transition-colors rounded-md"
      :class="headerClass"
      data-test-id="am3-collapsible--button"
      @click="toggleCollapse"
    >
      <div class="flex items-center gap-2 flex-1">
        <slot name="header">
          <span class="font-medium">{{ title }}</span>
        </slot>
      </div>
      <AM3Icon
        type="chevron-down"
        size="sm"
        class="transition-transform duration-200"
        :class="{ 'rotate-180': !isCollapsed }"
      />
    </button>

    <!-- Collapsible content -->
    <div
      v-show="!isCollapsed"
      class="transition-all duration-200 ease-in-out overflow-hidden"
      :class="contentClass"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'

interface Props {
  modelValue?: boolean
  title?: string
  defaultCollapsed?: boolean
  headerClass?: string
  contentClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  title: '',
  defaultCollapsed: false,
  headerClass: '',
  contentClass: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

// Internal collapsed state
const internalCollapsed = ref(props.defaultCollapsed)

// Use v-model if provided, otherwise use internal state
const isCollapsed = computed({
  get: () => props.modelValue !== undefined ? props.modelValue : internalCollapsed.value,
  set: (value: boolean) => {
    if (props.modelValue !== undefined) {
      emit('update:modelValue', value)
    }
    else {
      internalCollapsed.value = value
    }
  },
})

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
}
</script>
